import {
  AuthenticatedUser,
  FlightRoomUser,
  FlightRoomUserStatus,
  JobTitle,
  Message,
  MessageAttachment,
  MessageMention,
  MessageStatus,
  MessageTypes,
  MessageVisibility,
  ReceiveSocketMessage,
  SendSocketMessage,
} from '@connect-x-app/shared-domain-models';
import { IconName } from '@connect-x-app/shared-types';
import { ChatMessage } from '@connect-x-app/shared-view-models';

import { getIconForUser } from '../icons';
import { getCustomCrewTitle, replaceJobTitlePlaceholders } from '../strings';
import { UserUtil } from '../users';

export class MessageUtil {
  static processSystemMessage(input?: string | null): string {
    return replaceJobTitlePlaceholders(input);
  }
}

export const mapStringToMessage = (
  flightroomId: string,
  channelId: string,
  userId: string,
  clientMessageId: string,
  message: string,
  mentions: MessageMention[] | null,
  pendingImage?: File | null,
): Message => {
  const timestamp = new Date().toISOString();

  return {
    attachments: [],
    channelId,
    createdAt: timestamp,
    externalId: clientMessageId,
    id: '',
    level: null,
    mentions,
    pendingImage: pendingImage ?? undefined,
    receipts: [],
    room: flightroomId,
    status: MessageStatus.PENDING,
    text: message.trim(),
    type: MessageTypes.USER,
    user: userId,
    visibility: MessageVisibility.PRIVATE,
  };
};

export const mapMessageToSendSocketMessage = (message: Message, attachments: MessageAttachment[] = []): SendSocketMessage => {
  return {
    attachments,
    channelId: message.channelId,
    chatroomId: message.room,
    clientMessageId: message.externalId ?? message.id,
    createdAt: message.createdAt,
    ...(message.mentions?.length ? { mentions: message.mentions } : {}),
    text: message.text,
  };
};

export const mapSocketMessageToMessage = (socketMessage: ReceiveSocketMessage): Message => {
  return {
    attachments: socketMessage.attachments,
    channelId: socketMessage.channelId,
    createdAt: socketMessage.createdAt,
    externalId: socketMessage.externalMessageId ?? undefined,
    id: socketMessage.messageId,
    level: socketMessage.level,
    mentions: socketMessage.mentions,
    receipts: socketMessage.recipients.map(({ externalId }) => ({
      status: MessageStatus.SENT,
      updatedAt: socketMessage.createdAt,
      user: externalId,
    })),
    room: socketMessage.chatroomId,
    status: socketMessage.recipients.length > 0 ? MessageStatus.SENT : MessageStatus.READ,
    text: socketMessage.text,
    type: socketMessage.type,
    user: socketMessage.senderId,
    visibility: socketMessage.visibility,
  };
};

export const mapToChatMessage = (
  flightroomUsers: FlightRoomUser[] | undefined,
  users: FlightRoomUser[],
  message: Message,
  currentUser?: AuthenticatedUser | null,
): ChatMessage => {
  const isMe = currentUser ? message.user === currentUser.externalId : false;

  const user = users?.find(u => u.externalId === message.user);
  const roomUser = flightroomUsers?.find(u => u.externalId === message.user);

  const icon: IconName = getIconForUser({ crewMember: roomUser, user });

  const customCrewTitle = roomUser ? getCustomCrewTitle(roomUser.jobTitle, roomUser.jobTitleCustom) : null;

  const title: string =
    roomUser?.status === FlightRoomUserStatus.SCHEDULED
      ? (customCrewTitle ?? UserUtil.getDivision(roomUser.jobTitle))
      : UserUtil.getGuestUserString(roomUser?.jobTitle ?? user?.jobTitle ?? JobTitle.UNKNOWN, customCrewTitle);

  return {
    ...message,
    delivered: message.status === MessageStatus.DELIVERED,
    failed: message.status === MessageStatus.FAILED,
    isMe,
    pending: message.status === MessageStatus.PENDING || Boolean(message?.pendingImage),
    read: message.status === MessageStatus.READ,
    sent: message.status === MessageStatus.SENT,
    system: message.type === MessageTypes.SYSTEM,
    userInfo: {
      icon,
      id: message?.user ?? '',
      name: !user ? undefined : UserUtil.getFullName(user),
      title,
    },
  };
};

export const isPublicMessage = (visibility?: MessageVisibility): boolean => {
  return visibility !== MessageVisibility.PRIVATE;
};
